import { FC, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import { WordlistsApi } from 'src/api';
import { Creator } from 'src/api/wordlists/types';
import { ActionsPopup, Icon, InfoTooltipModal } from 'src/components';
import { useUserContext } from 'src/context/user';
import { shareUrl } from 'src/utils/share';
import { stringifyUrl } from 'src/utils/stringifyUrl';
import { ROUTES } from 'src/constants/routes.const';
import useSafePush from 'src/hooks/useSafePush';
import * as S from './styles';

const msgs = defineMessages({
  completed: { id: 'wordlist_completedscreen_label' },
  currentlyLearning: { id: 'wordlists_currentlylearning_label' },
  pro: { id: 'wordlists_viewscreen_PRO_badge' },
  wordsToLearn: { id: 'wordlists_viewlist_wordcount_label' },
  shareCta: { id: 'wordlists_viewscreen_share' },
  editWordlist: { id: 'wordlists_edit_button', defaultMessage: 'Edit Word List' },
  deleteWordlist: { id: 'wordlists_delete_confirm_header' },
  deleteConfirmHeader: { id: 'wordlists_delete_confirm_header' },
  deleteConfirmText: { id: 'wordlists_delete_confirm_text' },
  cancel: { id: 'wordlist_enrollment_cancel' },
  confirm: { id: 'resetprogress_delete_language_cta' },
  learnerCount: { id: 'wordlists_viewscreen_learner_count' },
  creatorName: { id: 'wordlists_viewscreen_author_name' },
  viewDescription: { id: 'wordlists_viewscreen_description', defaultMessage: 'View Description' },
});

interface Props {
  title: string;
  pictureUrl: string;
  isActive: boolean;
  isCompleted: boolean;
  isPremium: boolean;
  isLocked: boolean;
  wordsToLearn: number;
  creator: Creator;
  creatorId?: number | null;
  wordlistId?: string;
  languagePairId?: number;
  description?: string;
  creatorUsername?: string;
  userCollectionCount?: number;
}

export const ImageBanner: FC<Props> = ({
  title,
  pictureUrl,
  isActive,
  isCompleted,
  isPremium,
  isLocked,
  wordsToLearn,
  creator,
  creatorId,
  wordlistId,
  languagePairId,
  description,
  creatorUsername,
  userCollectionCount,
}) => {
  const intl = useIntl();
  const router = useRouter();
  const { user } = useUserContext();
  const { safePush } = useSafePush();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const isProUser = user?.isPro;
  const showCompletedLabel = isCompleted && creator === 'memrise';
  const canEdit = user?.id === creatorId;

  const handleShareClick = async () => {
    /* istanbul ignore if: defensive check or SSR */
    if (typeof window === 'undefined') return;

    const shareableUrl =
      wordlistId && languagePairId
        ? `${window.location.origin}${stringifyUrl(`${ROUTES.wordlists.details}/${wordlistId}`, {
            languagePairId: String(languagePairId),
          })}`
        : window.location.href;

    await shareUrl(shareableUrl);
  };

  const handleEditClick = () => {
    void router.push(
      stringifyUrl(`${ROUTES.wordlists.create}/${wordlistId}`, {
        languagePairId: String(languagePairId),
      }),
    );
  };

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!wordlistId || !languagePairId) return;

    await WordlistsApi.deleteWordlist(languagePairId, wordlistId);
    setIsDeleteModalOpen(false);

    void safePush(
      stringifyUrl(ROUTES.wordlists.main, {
        languagePairId: String(languagePairId),
      }),
      true,
    );
  };

  return (
    <S.Container data-testid="image-banner">
      <S.CoverImage src={pictureUrl} alt="" data-testid="image-banner-cover" />
      <S.CoverChrome
        $noOverlay={creator === 'user' && !creatorId}
        data-testid="image-banner-chrome"
      >
        <S.ShareButton
          icon="share"
          buttonSize="small"
          buttonType="secondary"
          onClick={handleShareClick}
        >
          {intl.formatMessage(msgs.shareCta)}
        </S.ShareButton>
        <S.Title data-testid={`wordlist_${title}`}>{title}</S.Title>
        {description && (
          <S.DescriptionContainer data-testid="view-description">
            <InfoTooltipModal
              tooltip={description}
              label={intl.formatMessage(msgs.viewDescription)}
            />
          </S.DescriptionContainer>
        )}
        <S.BadgeContainer>
          {isActive && (
            <S.ActiveBadge data-testid="wordlist_currently_learning_label">
              {intl.formatMessage(msgs.currentlyLearning)}
            </S.ActiveBadge>
          )}
          {isPremium && (isProUser || isLocked) && (
            <S.ProBadge>
              {!isProUser && isLocked && <Icon name="padlock" />}
              {intl.formatMessage(msgs.pro)}
            </S.ProBadge>
          )}
          {showCompletedLabel && (
            <S.CompletedBadge data-testid="wordlist_completed_label">
              <Icon name="tickFilled" />
              {intl.formatMessage(msgs.completed)}
            </S.CompletedBadge>
          )}
          {userCollectionCount !== undefined && (
            <S.LearnerCountBadge data-testid="learner-count">
              <Icon name="community" />
              {intl.formatMessage(msgs.learnerCount, { LearnerCount: userCollectionCount })}
            </S.LearnerCountBadge>
          )}
          {creatorUsername && (
            <S.CreatorBadge data-testid="creator-name">
              <Icon name="edit" />
              {intl.formatMessage(msgs.creatorName, { creator: creatorUsername })}
            </S.CreatorBadge>
          )}
          <S.WordCount>{intl.formatMessage(msgs.wordsToLearn, { n: wordsToLearn })}</S.WordCount>
        </S.BadgeContainer>
        {canEdit && (
          <>
            <S.DeleteButton
              icon="deleteIcon"
              handleClick={handleDeleteClick}
              aria-label={intl.formatMessage(msgs.deleteWordlist)}
              data-testid="delete-wordlist-button"
            />
            <S.EditButton
              icon="edit"
              handleClick={handleEditClick}
              aria-label={intl.formatMessage(msgs.editWordlist)}
              data-testid="edit-wordlist-button"
            />
            <ActionsPopup
              isOpen={isDeleteModalOpen}
              onClose={handleCloseDeleteModal}
              header={intl.formatMessage(msgs.deleteConfirmHeader)}
              text={intl.formatMessage(msgs.deleteConfirmText)}
              secondaryBtnText={intl.formatMessage(msgs.cancel)}
              secondaryBtnAction={handleCloseDeleteModal}
              primaryBtnText={intl.formatMessage(msgs.confirm)}
              primaryBtnAction={handleConfirmDelete}
            />
          </>
        )}
      </S.CoverChrome>
    </S.Container>
  );
};
